/**
 * O ASSISTENTE: do pedido em português ao botão que abre o modal certo.
 *
 * A IA (Haiku + Jev, na Edge Function `assistente`) só ENTENDE: devolve qual
 * fluxo a pessoa quer e o que ela citou (cliente, número do serviço, etapa).
 * Tudo o que vem depois mora aqui, sem IA: achar os serviços, ver em quais o
 * fluxo cabe, perguntar "qual dos dois?" e montar os botões. É rápido, não
 * custa nada e usa as mesmas regras das telas — o assistente nunca oferece
 * refaturar um serviço que a tela não deixaria refaturar.
 *
 * O assistente NÃO preenche nada. Cada botão abre o modal que a pessoa já
 * conhece; lançar, emitir e confirmar continuam lá, com as travas de sempre.
 *
 * A lista de fluxos é repetida em `supabase/functions/assistente/index.ts`
 * (a Edge Function não importa daqui); `fluxos.test.js` confere que as duas
 * batem.
 */
import {
  SCHEDULE_BY_ID, SCHEDULE_CHAIN, etapasContratadas, etapaParaAbrir, todasAsEtapasConcluidas,
} from '../../constants/schedule';
import { entradaAEmitir } from '../calculations/entrada';
import { temFichaDeCampo, etapaDaFicha } from '../fichaDeCampo';
import { pendencias, grupoDoLote, prontoParaFaturar, reais } from './pendencias';
import { prenhasDeAgora, prenhasPorCliente, resumoDasPrenhas } from '../calculations/prenhas';

export const FLUXOS = {
  ver_servico:       { botao: () => 'Ver serviço', servico: true },
  listar_servicos:   { botao: () => 'Listar serviços', servico: false },
  listar_prenhas:    { botao: () => 'Receptoras prenhas', servico: false },
  agenda:           { botao: () => 'Agenda', servico: false },
  ver_cliente:       { botao: () => 'Ver cliente', servico: false },
  ver_estoque:       { botao: () => 'Ir para Estoque', servico: false },
  ver_animal:        { botao: () => 'Ir para Animais', servico: false },
  pendencias:        { botao: () => 'Pendências', servico: false },
  abrir_etapa:       { botao: (e) => `Abrir ${rotuloDaEtapa(e) || 'o workflow'}`, servico: true },
  ficha_de_campo:    { botao: (e) => `Ficha de campo${e ? ` ${daEtapa(e)}` : ''}`, servico: true },
  emitir_entrada:    { botao: () => 'Emitir entrada', servico: true },
  refaturar_entrada: { botao: () => 'Refaturar entrada', servico: true },
  faturar:           { botao: () => 'Faturar', servico: true },
  refaturar:         { botao: () => 'Refaturar', servico: true },
  receber:           { botao: () => 'Abrir financeiro', servico: true },
  custos:            { botao: () => 'Lançar custos', servico: true },
  editar_servico:    { botao: () => 'Editar cadastro', servico: true },
  novo_servico:      { botao: () => 'Novo serviço', servico: false },
  novo_cliente:      { botao: () => 'Cadastrar cliente', servico: false },
  editar_cliente:    { botao: () => 'Editar cliente', servico: false },
  ir_para_aba:       { botao: (_, aba) => `Ir para ${ABAS[aba] || 'a aba'}`, servico: false },
  conversa:          { botao: () => '', servico: false },
};

/**
 * Ações que só existem como botão, depois de um cartão: a IA nunca as
 * escolhe, então não estão em FLUXOS nem na Edge Function.
 */
export const ACOES_DE_BOTAO = [
  'estoque_do_cliente', 'cruzamentos_do_animal', 'fila', 'fila_proxima', 'fila_pular', 'fila_parar',
];

export const ABAS = {
  dashboard: 'o Painel', clients: 'Clientes', estoque: 'Estoque', insumos: 'Insumos',
  financial: 'Financeiro', animals: 'Animais', relatorios: 'Relatórios',
};

export const ETAPAS = ['protocolo', 'opu', 'fiv', 'te', 'dg30', 'dg60', 'relatorios'];

export const rotuloDaEtapa = (id) =>
  (id === 'relatorios' ? 'relatório de visita' : SCHEDULE_BY_ID[id]?.label || null);

/** "do DG30", "da TE": o DG é masculino, as outras etapas são femininas. */
const daEtapa = (id) => `${id === 'dg30' || id === 'dg60' ? 'do' : 'da'} ${rotuloDaEtapa(id)}`;

/** Sem acento, minúsculo: "João" acha "JOAO". */
export const normalizar = (s) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const dataCurta = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`)
  .toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : null);

/** Onde o serviço está, em poucas palavras: a última etapa feita. */
const ondeEsta = (s) => {
  const feitas = etapasContratadas(s).filter(e => e.done);
  return feitas.length ? `${feitas[feitas.length - 1].label} feita` : 'nada lançado';
};

export const rotuloDoServico = (s) =>
  [s.service_code || `#${s.numero || ''}`, dataCurta(s.date), ondeEsta(s)].filter(Boolean).join(' · ');

/**
 * Os serviços a que o pedido se refere.
 *
 * Número citado manda: "87", "0087" e "2026-0087" acham o 2026-0087. Sem
 * número, o nome do cliente — cada palavra do que a pessoa disse tem que
 * aparecer no nome, em qualquer ordem ("francisco lara", "lara").
 */
export const servicosCitados = (services = [], { servico, cliente } = {}, clients = []) => {
  const num = String(servico || '').replace(/\D/g, '');
  if (num) {
    const n = Number(num.slice(-4));
    const achados = services.filter(s => {
      const codigo = String(s.service_code || '').replace(/\D/g, '');
      return codigo === num || Number(codigo.slice(-4)) === n || Number(s.numero) === n;
    });
    if (achados.length > 0) return achados;
  }
  const palavras = normalizar(cliente).split(/\s+/).filter(p => p.length >= 2);
  if (palavras.length === 0) return [];
  const casa = porNomeDoCliente(palavras, clients);
  return services
    .filter(casa)
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
};

/**
 * O serviço é do cliente citado? Confere o nome gravado no serviço E o do
 * cadastro. Não bastava o do serviço: ali às vezes está a fazenda — dos 4
 * serviços do Alencar, 2 diziam "Fazenda Serra Bonita", e "os serviços do
 * alencar" trazia só 2 (30/10/2026).
 */
const porNomeDoCliente = (palavras, clients = []) => {
  const cadastro = new Map(clients.map(c => [c.id, normalizar(c.name)]));
  const tem = (nome) => !!nome && palavras.every(p => nome.includes(p));
  return (s) => tem(normalizar(s.client_name)) || tem(cadastro.get(s.client_id));
};

/** O nome do cliente do serviço: o do cadastro, e o do serviço na falta dele. */
const nomeDoCliente = (s, clients = []) =>
  clients.find(c => c.id === s.client_id)?.name || s.client_name;

const clientesCitados = (clients = [], cliente) => {
  const palavras = normalizar(cliente).split(/\s+/).filter(p => p.length >= 2);
  if (palavras.length === 0) return [];
  return clients.filter(c => { const nome = normalizar(c.name); return palavras.every(p => nome.includes(p)); });
};

/**
 * Em quais serviços o fluxo cabe AGORA, e por que não, quando não cabe.
 * As regras são as das telas: o menu ⋯ do Financeiro e o convite da TE.
 */
const cabe = (fluxo, s, ctx, etapa) => {
  const parcelas = ctx.parcelasPorServico?.[s.id] || [];
  const doServico = parcelas.filter(p => !p.ehEntrada);
  const daEntrada = parcelas.filter(p => p.ehEntrada);
  switch (fluxo) {
    case 'refaturar':
      if (!s.billed) return 'ainda não tem fatura do serviço';
      return doServico.some(p => p.paga_em) ? 'tem parcela da fatura já paga' : null;
    case 'refaturar_entrada':
      if (daEntrada.length === 0) return 'não tem entrada emitida';
      return daEntrada.some(p => p.paga_em) ? 'tem a entrada já paga' : null;
    case 'emitir_entrada': {
      const jaEmitida = Boolean(ctx.entradaEmitidaPorServico?.[s.id]);
      const { deve, motivo } = entradaAEmitir(s, ctx.lab, jaEmitida);
      return deve ? null : (MOTIVO_CURTO[motivo] || 'não tem entrada a emitir');
    }
    case 'faturar':
      if (s.billed) return 'já está faturado';
      return (ctx.valorDoServico?.(s) || 0) > 0 ? null : 'ainda não tem valor a faturar';
    case 'abrir_etapa':
      if (!etapa || etapa === 'relatorios') return null;
      return etapasContratadas(s).some(e => e.id === etapa) ? null : `não tem ${rotuloDaEtapa(etapa)} contratada`;
    case 'ficha_de_campo':
      return etapaDaFicha(s, etapa) ? null : 'não tem ficha de campo pendente';
    default:
      return null;
  }
};

/** Por que o fluxo não cabe no serviço agora; nulo quando cabe. A fila usa. */
export const porQueNaoCabe = (fluxo, s, ctx, etapa) => cabe(fluxo, s, ctx, etapa);

const MOTIVO_CURTO = {
  te_em_andamento: 'a TE ainda não foi concluída',
  sem_transferidos: 'não tem embrião transferido',
  sem_taxa: 'está sem taxa de entrada',
  ja_emitida: 'já tem a entrada emitida',
  servico_faturado: 'já está faturado',
  servico_sem_cobranca: 'é sem cobrança',
  diaria_nao_adianta: 'é por diária',
  faturamento_fecha_antes: 'fatura antes da TE, não tem entrada',
};

const acao = (fluxo, extra = {}) => ({ fluxo, ...extra });
const botao = (fluxo, extra = {}, rotulo = null) => ({
  rotulo: rotulo || FLUXOS[fluxo].botao(extra.etapa, extra.aba), acao: acao(fluxo, extra),
});

const AJUDA = 'Posso te levar direto ao lugar certo. Por exemplo: "lista os serviços do Vitor", '
  + '"o que tem pra amanhã?", "refaturar o serviço do Francisco", "abrir a TE do 0087", '
  + '"me traz as pendências" ou "emitir todas as entradas".';

/**
 * A resposta do assistente: texto curto e botões.
 *
 * `decisao` é o que a Edge Function devolveu: { fluxo, cliente, servico,
 * etapa, aba }. `ctx` é o estado que o FIVSystem já tem carregado.
 */
export const responder = (decisao = {}, ctx = {}) => {
  const { cliente, servico, etapa, aba, animal } = decisao;
  // Pedido vago que cita cliente, número ou animal ("queria dar uma olhada no
  // serviço do Vitor Ventura") é ver aquilo, não conversa: a IA classificou
  // assim no primeiro teste (29/10/2026) e a pessoa recebeu o texto de ajuda.
  const vago = !decisao.fluxo || decisao.fluxo === 'conversa' || !FLUXOS[decisao.fluxo];
  const fluxo = !vago ? decisao.fluxo
    : servico || cliente ? 'ver_servico'
    : animal ? 'ver_animal'
    : 'conversa';
  const def = FLUXOS[fluxo] || FLUXOS.conversa;

  if (fluxo === 'conversa' || !FLUXOS[fluxo]) return { texto: AJUDA, botoes: [] };
  if (fluxo === 'ver_animal') return verAnimal(animal, ctx);
  if (fluxo === 'pendencias') return cartaoDePendencias(ctx, cliente);
  if (fluxo === 'agenda') return agenda(ctx, decisao.periodo);
  if (fluxo === 'listar_servicos') return listarServicos(ctx, { cliente, situacao: decisao.situacao, etapa });
  if (fluxo === 'listar_prenhas') return listarPrenhas(ctx, { cliente, servico });
  if (decisao.todos && LOTE.includes(fluxo)) return lote(fluxo, ctx, { cliente, etapa });
  if (fluxo === 'ver_cliente' || fluxo === 'ver_estoque') {
    // "O cliente dele": sem nome, vale o cliente do serviço em foco.
    const doFoco = !cliente && ctx.foco ? (ctx.services || []).find(x => x.id === ctx.foco) : null;
    const achados = doFoco
      ? (ctx.clients || []).filter(c => c.id === doFoco.client_id)
      : clientesCitados(ctx.clients, cliente);
    if (fluxo === 'ver_estoque' && achados.length === 0 && !cliente) {
      return { texto: '', botoes: [botao('ir_para_aba', { aba: 'estoque' })] };
    }
    if (achados.length === 0) {
      return { texto: cliente ? `Não achei o cliente "${cliente}".` : 'De qual cliente?', botoes: [] };
    }
    if (achados.length === 1) {
      return fluxo === 'ver_cliente' ? cartaoDoCliente(achados[0], ctx) : cartaoDoEstoque(achados[0]);
    }
    return {
      texto: `Achei ${achados.length} clientes. Qual?`,
      botoes: achados.slice(0, 8).map(c => (fluxo === 'ver_cliente'
        ? botao('ver_cliente', { clientId: c.id }, c.name)
        : botao('estoque_do_cliente', { clientId: c.id }, `Estoque · ${c.name}`))),
    };
  }
  if (fluxo === 'ir_para_aba') {
    return ABAS[aba]
      ? { texto: '', botoes: [botao('ir_para_aba', { aba })] }
      : { texto: 'Para qual área? Estoque, Animais, Financeiro, Clientes, Relatórios ou Insumos.', botoes: [] };
  }
  if (fluxo === 'novo_cliente') return { texto: '', botoes: [botao('novo_cliente')] };
  if (fluxo === 'novo_servico' || fluxo === 'editar_cliente') {
    const achados = clientesCitados(ctx.clients, cliente);
    if (fluxo === 'novo_servico' && achados.length === 0) {
      return { texto: cliente ? `Não achei o cliente "${cliente}". Abro o serviço em branco?` : '',
        botoes: [botao('novo_servico')] };
    }
    if (achados.length === 0) return { texto: cliente ? `Não achei o cliente "${cliente}".` : 'De qual cliente?', botoes: [] };
    return {
      texto: achados.length > 1 ? `Achei ${achados.length} clientes. Qual?` : '',
      botoes: achados.slice(0, 6).map(c => botao(fluxo, { clientId: c.id },
        achados.length > 1 ? c.name : `${def.botao()} · ${c.name}`)),
    };
  }

  // Daqui para baixo, o fluxo é sobre um serviço. Sem cliente nem número, é o
  // serviço em foco: o último que a conversa escolheu ("refatura a entrada").
  const emFoco = !servico && !cliente && ctx.foco
    ? (ctx.services || []).filter(s => s.id === ctx.foco) : [];
  const citados = emFoco.length ? emFoco : servicosCitados(ctx.services, { servico, cliente }, ctx.clients);

  /*
   * Direto ao cartão só quando o cliente tem UM serviço. Antes ia direto
   * quando havia um só EM ABERTO, e os outros sumiam sem aviso: a pessoa
   * pediu para ver os serviços e recebeu um (teste de 29/10/2026). Agora os
   * em aberto vêm primeiro e os fechados depois, marcados.
   */
  if (fluxo === 'ver_servico' && citados.length > 0) {
    if (citados.length === 1) return cartaoDoServico(citados[0], ctx);
    const abertos = citados.filter(emAberto);
    return {
      texto: `${nomeDoCliente(citados[0], ctx.clients)} · ${citados.length} serviços${abertos.length ? `, ${abertos.length} em aberto` : ', nenhum em aberto'}`,
      botoes: [],
      bloco: { tipo: 'servicos', ids: abertosPrimeiro(citados).map(x => x.id) },
    };
  }
  if (citados.length === 0) {
    return {
      texto: servico || cliente
        ? `Não achei serviço ${servico ? `com o número ${servico}` : `do cliente "${cliente}"`}.`
        : 'De qual serviço? Me diz o cliente ou o número.',
      botoes: [],
    };
  }

  const comMotivo = citados.map(s => ({ s, motivo: cabe(fluxo, s, ctx, etapa) }));
  const cabem = comMotivo.filter(x => !x.motivo).map(x => x.s);
  const extra = (s) => ({ serviceId: s.id, etapa: fluxo === 'ficha_de_campo' ? etapaDaFicha(s, etapa) : etapa });

  if (cabem.length === 0) {
    const nome = nomeDoCliente(citados[0], ctx.clients);
    const porque = comMotivo.slice(0, 3).map(({ s, motivo }) => `${s.service_code}: ${motivo}`).join('; ');
    return {
      texto: `Não dá para ${VERBO[fluxo] || 'fazer isso'} ${citados.length > 1 ? `nos serviços de ${nome}` : `no ${citados[0].service_code}`} agora (${porque}).`,
      botoes: citados.slice(0, 3).map(s => botao('receber', { serviceId: s.id }, `Financeiro · ${s.service_code}`)),
    };
  }
  if (cabem.length === 1) {
    const s = cabem[0];
    return { texto: `${s.client_name} · ${rotuloDoServico(s)}`, botoes: [botao(fluxo, extra(s))], foco: s.id };
  }
  // Vários: a lista, com a ação na própria linha de cada um.
  return {
    texto: `${nomeDoCliente(cabem[0], ctx.clients)} · ${cabem.length} serviços onde dá para ${VERBO[fluxo] || 'fazer isso'}. Qual?`,
    botoes: [],
    bloco: {
      tipo: 'servicos', ids: cabem.map(s => s.id),
      acoes: Object.fromEntries(cabem.map(s => [s.id, botao(fluxo, extra(s))])),
    },
  };
};

/**
 * O CARTÃO DO CLIENTE: um bloco que a tela desenha — o nome, a lista dos
 * serviços dele e o que se faz com o próprio cliente.
 */
export const cartaoDoCliente = (c) => ({ texto: '', botoes: [], bloco: { tipo: 'cliente', id: c.id } });

/** O que se faz com um cliente, para o cartão dele. */
export const acoesDoCliente = (c) => [
  botao('novo_servico', { clientId: c.id }, 'Novo serviço'),
  botao('estoque_do_cliente', { clientId: c.id }, 'Estoque'),
  botao('editar_cliente', { clientId: c.id }, 'Editar cliente'),
];

/** Os serviços de um cliente, mais recentes primeiro. */
export const servicosDoCliente = (ctx, clientId) => (ctx.services || [])
  .filter(x => x.client_id === clientId)
  .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

/** Em aberto primeiro, cada metade na ordem em que veio. */
export const abertosPrimeiro = (lista) => [...lista.filter(emAberto), ...lista.filter(x => !emAberto(x))];

/** O estoque de um cliente: abre a ficha do botijão dele. */
export const cartaoDoEstoque = (c) => ({
  texto: '',
  botoes: [
    botao('estoque_do_cliente', { clientId: c.id }, `Estoque de ${c.name}`),
    botao('ir_para_aba', { aba: 'estoque' }, 'Botijão inteiro'),
  ],
});

const CATEGORIA = { Doadora: 'doadora', Receptora: 'receptora', Reprodutor: 'touro' };

/** O cartão do animal, com o que vem da busca (brinco, raça, categoria, RGD). */
export const cartaoDoAnimal = (a) => ({
  texto: [a.identifier, CATEGORIA[a.category] || a.category, a.breed, a.rgd && `RGD ${a.rgd}`]
    .filter(Boolean).join(' · '),
  botoes: [
    botao('cruzamentos_do_animal', { animal: a }, 'Cruzamentos'),
    botao('ir_para_aba', { aba: 'animals' }, 'Ir para Animais'),
  ],
});

/**
 * Animal pelo brinco ou RGD. A busca é do banco (`ctx.animais`, feita pelo
 * FIVSystem antes de responder); o brinco exato vence os parecidos.
 */
const verAnimal = (termo, ctx) => {
  if (!termo) {
    return { texto: 'Qual animal? Me diz o brinco ou o RGD.', botoes: [botao('ir_para_aba', { aba: 'animals' })] };
  }
  const achados = ctx.animais || [];
  const exatos = achados.filter(a => normalizar(a.identifier) === normalizar(termo)
    || normalizar(a.rgd) === normalizar(termo));
  const lista = exatos.length ? exatos : achados;
  if (lista.length === 0) {
    return { texto: `Não achei animal "${termo}".`, botoes: [botao('ir_para_aba', { aba: 'animals' })] };
  }
  if (lista.length === 1) return cartaoDoAnimal(lista[0]);
  return {
    texto: `Achei ${lista.length} animais com "${termo}". Qual?`,
    botoes: lista.slice(0, 8).map(a => botao('ver_animal', { animal: a },
      [a.identifier, CATEGORIA[a.category] || a.category].filter(Boolean).join(' · '))),
  };
};

/** Fluxos que fazem sentido "para todos": viram fila. */
const LOTE = ['emitir_entrada', 'faturar', 'receber', 'abrir_etapa'];

const doCliente = (itens, cliente, clients = []) => {
  const palavras = normalizar(cliente).split(/\s+/).filter(p => p.length >= 2);
  if (palavras.length === 0) return itens;
  const casa = porNomeDoCliente(palavras, clients);
  return itens.filter(i => casa(i.s));
};

/** O botão que começa a fila de um grupo. */
const botaoDaFila = (g, itens, rotulo) => botao('fila', {
  alvo: g.alvo,
  itens: itens.map(i => ({ serviceId: i.s.id, etapa: i.etapa || g.etapa || null })),
}, rotulo);


/**
 * O RESUMO DE PENDÊNCIAS: um bloco com uma seção por tipo, que a tela
 * desenha com os dados de agora. Com cliente citado, só as dele.
 */
export const cartaoDePendencias = (ctx = {}, cliente = null) => {
  const quem = cliente ? ` de ${cliente}` : '';
  if (gruposDePendencias(ctx, cliente).length === 0) return { texto: `Nada pendente${quem} agora.`, botoes: [] };
  return { texto: '', botoes: [], bloco: { tipo: 'pendencias', cliente: cliente || null } };
};

/** Os grupos de pendência, já filtrados pelo cliente. */
export const gruposDePendencias = (ctx = {}, cliente = null) => pendencias(ctx)
  .map(g => ({ ...g, itens: doCliente(g.itens, cliente, ctx.clients) }))
  .filter(g => g.itens.length);

/** O botão "fazer em sequência" de um grupo de pendências. */
export const filaDoGrupo = (g, rotulo) => botaoDaFila(g, g.itens, rotulo);

/**
 * "Emitir todas as entradas pendentes": a lista e o botão que começa a fila.
 * Não emite nada — cada item abre o modal de sempre, e a pessoa confirma um
 * por um. Lote de documento para cliente num clique só espalharia um erro por
 * todos de uma vez.
 */
const lote = (fluxo, ctx, { cliente, etapa }) => {
  const g = grupoDoLote(fluxo, ctx, { etapa });
  const itens = g ? doCliente(g.itens, cliente, ctx.clients) : [];
  const NOME = {
    emitir_entrada: ['entrada a emitir', 'entradas a emitir'],
    faturar: ['serviço pronto para faturar', 'serviços prontos para faturar'],
    receber: ['serviço com parcela vencida', 'serviços com parcela vencida'],
    abrir_etapa: [`${etapa ? rotuloDaEtapa(etapa) : 'etapa'} com data passada`,
      `${etapa ? rotuloDaEtapa(etapa) : 'etapas'} com data passada`],
  }[fluxo];
  const quem = cliente ? ` de ${cliente}` : '';
  if (itens.length === 0) return { texto: `Nenhum(a) ${NOME[0]}${quem} agora.`, botoes: [] };
  const valor = itens.reduce((t, i) => t + (Number(i.valor) || 0), 0);
  return {
    texto: `${itens.length} ${NOME[itens.length === 1 ? 0 : 1]}${quem}`
      + `${valor ? `, ${reais(valor)} no total` : ''}. Abro um por vez; você confere e confirma cada um.`,
    botoes: [
      botaoDaFila({ ...g, etapa: g.etapa || etapa }, itens, `Começar: ${itens[0].s.service_code} (1 de ${itens.length})`),
    ],
    bloco: { tipo: 'servicos', ids: itens.map(i => i.s.id), notas: Object.fromEntries(itens.map(i => [i.s.id, i.detalhe])) },
  };
};

/** Em aberto: falta etapa ou falta receber. */
export const emAberto = (s) => !(todasAsEtapasConcluidas(s) && s.paid);

/** Onde o dinheiro do serviço está, em poucas palavras. */
export const situacaoFinanceira = (s, ctx) => {
  if (s.paid) return 'pago';
  if (s.billed) return 'faturado, a receber';
  return ctx.entradaEmitidaPorServico?.[s.id] ? 'entrada emitida, serviço a faturar' : 'não faturado';
};

/**
 * O CARTÃO DE UM SERVIÇO: um bloco que a tela desenha com as etapas em linha
 * do tempo, a situação financeira, o próximo passo à vista e o resto em
 * linhas por seção (`acoesDoServico`). O serviço vira o foco da conversa.
 */
export const cartaoDoServico = (s) => ({ texto: '', botoes: [], bloco: { tipo: 'servico', id: s.id }, foco: s.id });

const hojeLocal = () => {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};

/**
 * As etapas contratadas para a linha do tempo: feita (com a data), agendada
 * (com a data, atrasada se já passou) ou só pendente.
 */
export const etapasDoServico = (s, hoje = hojeLocal()) => etapasContratadas(s).map(e => {
  const feita = s[e.actualField] ? String(s[e.actualField]).slice(0, 10) : null;
  const agendada = s[e.field] ? String(s[e.field]).slice(0, 10) : null;
  return {
    id: e.id, label: e.label, feita: e.done,
    data: e.done ? feita : agendada,
    atrasada: !e.done && Boolean(agendada) && agendada < hoje,
  };
});

/**
 * O que se faz com um serviço: o próximo passo (à vista, em destaque) e o
 * resto que cabe AGORA, em seções — pelas mesmas regras das telas.
 */
export const acoesDoServico = (s, ctx = {}) => {
  const principais = proximosPassos(s, ctx);
  const ja = (fluxo, etapa = null) => principais.some(b => b.acao.fluxo === fluxo && (b.acao.etapa || null) === etapa);
  const se = (fluxo, rotulo, extra = {}) => (!ja(fluxo, extra.etapa || null) && !cabe(fluxo, s, ctx, extra.etapa || null)
    ? [botao(fluxo, { serviceId: s.id, ...extra }, rotulo)] : []);
  const fichas = ETAPAS.filter(e => temFichaDeCampo(e) && etapasContratadas(s).some(x => x.id === e))
    .flatMap(e => se('ficha_de_campo', `Ficha ${daEtapa(e)}`, { etapa: e }));
  return {
    principais,
    secoes: [
      { titulo: 'Etapas', botoes: [...se('abrir_etapa', 'Abrir workflow'), ...fichas] },
      { titulo: 'Financeiro', botoes: [
        ...se('receber', 'Abrir financeiro'), ...se('refaturar', 'Refaturar'),
        ...se('refaturar_entrada', 'Refaturar entrada'), ...(s.billed ? [] : se('custos', 'Lançar custos')),
      ] },
      { titulo: 'Cadastro', botoes: [
        ...se('editar_servico', 'Editar cadastro'),
        ...(s.client_id ? [botao('ver_cliente', { clientId: s.client_id }, 'Ver cliente')] : []),
      ] },
    ].filter(x => x.botoes.length),
  };
};

/*
 * LISTAR — o pedido mais óbvio, que faltou na primeira versão: "lista os
 * serviços do Vitor", "serviços em aberto", "quem está esperando DG30".
 *
 * Sem filtro de situação: com cliente, todos os dele (em aberto primeiro);
 * sem cliente, só os em aberto — o laboratório inteiro de uma vez é a aba
 * Dashboard, não uma resposta.
 */
const SITUACAO = {
  em_aberto: { teste: (s) => emAberto(s), nome: 'em aberto' },
  todos: { teste: () => true, nome: '' },
  a_faturar: { teste: (s, ctx) => prontoParaFaturar(s, ctx), nome: 'prontos para faturar' },
  atrasados: { teste: (s) => etapasDoServico(s).some(e => e.atrasada), nome: 'com etapa atrasada' },
};

const listarServicos = (ctx, { cliente, situacao, etapa }) => {
  const base = cliente ? servicosCitados(ctx.services, { cliente }, ctx.clients) : (ctx.services || []);
  if (cliente && base.length === 0) return { texto: `Não achei serviço do cliente "${cliente}".`, botoes: [] };
  const sit = SITUACAO[situacao] || (cliente ? SITUACAO.todos : SITUACAO.em_aberto);
  // "Esperando DG30": a PRÓXIMA etapa pendente dele é essa.
  const naEtapa = (s) => !etapa || etapasDoServico(s).find(e => !e.feita)?.id === etapa;
  const lista = abertosPrimeiro(base
    .filter(s => sit.teste(s, ctx) && naEtapa(s))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))));
  const descricao = [
    cliente ? nomeDoCliente(base[0], ctx.clients) : 'Serviços',
    sit.nome, etapa && `esperando ${rotuloDaEtapa(etapa)}`,
  ].filter(Boolean).join(' · ');
  if (lista.length === 0) return { texto: `${descricao}: nenhum.`, botoes: [] };
  return {
    texto: `${descricao} · ${lista.length} serviço${lista.length > 1 ? 's' : ''}`,
    botoes: [],
    bloco: { tipo: 'servicos', ids: lista.map(s => s.id) },
  };
};

/**
 * PRENHAS — "receptoras prenhas do Vitor", "quantas prenhas tem", "quem vai
 * parir", "as prenhas do 0087". O FIVSystem busca os diagnósticos antes de
 * responder (`ctx.prenhas`), como faz com o animal. A leitura que falhou chega
 * nula, e diz que falhou: "nenhuma prenha" seria uma resposta falsa.
 *
 * As linhas vão no próprio bloco: não há cópia delas no estado da tela.
 */
const listarPrenhas = (ctx, { cliente, servico }) => {
  if (!ctx.prenhas) return { texto: 'Não consegui ler as receptoras prenhas agora. Tenta de novo?', botoes: [] };

  const palavras = normalizar(cliente).split(/\s+/).filter(p => p.length >= 2);
  const doServico = servico
    ? new Set(servicosCitados(ctx.services, { servico }, ctx.clients).map(s => s.id)) : null;
  const casaCliente = porNomeDoCliente(palavras, ctx.clients);
  const prenhas = prenhasDeAgora(ctx.prenhas).filter(p => (doServico
    ? doServico.has(p.service_id)
    : palavras.length === 0 || casaCliente(p)));

  const de = servico ? ` do serviço ${servico}` : cliente ? ` de ${cliente}` : '';
  if (prenhas.length === 0) return { texto: `Nenhuma receptora prenha${de} agora.`, botoes: [] };

  const grupos = prenhasPorCliente(prenhas);
  const r = resumoDasPrenhas(prenhas);
  const quem = grupos.length === 1 ? grupos[0].client_name : `${grupos.length} clientes`;
  return {
    texto: [
      `${quem} · ${r.total} prenha${r.total > 1 ? 's' : ''}`,
      `${r.confirmadas} confirmada${r.confirmadas === 1 ? '' : 's'} na DG60`,
      r.aguardam > 0 && `${r.aguardam} aguarda${r.aguardam === 1 ? '' : 'm'} DG60`,
    ].filter(Boolean).join(' · '),
    botoes: [],
    bloco: { tipo: 'prenhas', grupos },
  };
};

/**
 * AGENDA — "o que tem pra hoje / amanhã / essa semana": as etapas pendentes
 * agendadas no período, uma linha por serviço, com a etapa e o dia na nota.
 */
const somarDias = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};

export const agenda = (ctx = {}, periodo = 'hoje', hoje = hojeLocal()) => {
  const [de, ate, nome] = {
    amanha: [somarDias(hoje, 1), somarDias(hoje, 1), 'Amanhã'],
    semana: [hoje, somarDias(hoje, 6), 'Próximos 7 dias'],
  }[periodo] || [hoje, hoje, 'Hoje'];
  const itens = [];
  (ctx.services || []).forEach(s => etapasDoServico(s, hoje).forEach(e => {
    if (!e.feita && e.data && e.data >= de && e.data <= ate) itens.push({ s, e });
  }));
  itens.sort((a, b) => a.e.data.localeCompare(b.e.data));
  if (itens.length === 0) return { texto: `${nome}: nada agendado.`, botoes: [] };
  const notas = {};
  itens.forEach(({ s, e }) => {
    const nota = `${e.label} ${e.data.slice(8, 10)}/${e.data.slice(5, 7)}`;
    notas[s.id] = notas[s.id] ? `${notas[s.id]} · ${nota}` : nota;
  });
  const ids = [...new Set(itens.map(x => x.s.id))];
  return {
    texto: `${nome}: ${itens.length} etapa${itens.length > 1 ? 's' : ''} em ${ids.length} serviço${ids.length > 1 ? 's' : ''}`,
    botoes: [],
    bloco: { tipo: 'servicos', ids, notas },
  };
};

const VERBO = {
  abrir_etapa: 'abrir essa etapa', ficha_de_campo: 'imprimir a ficha', emitir_entrada: 'emitir a entrada',
  refaturar_entrada: 'refaturar a entrada', faturar: 'faturar', refaturar: 'refaturar',
  receber: 'ver o financeiro', custos: 'lançar custos', editar_servico: 'editar o cadastro',
};

/**
 * A CADEIA: o que o serviço libera depois de uma ação.
 *
 * Não executa nada — são sugestões, cada uma um botão. A próxima etapa
 * contratada (e a ficha de campo dela), a entrada quando a TE a libera, o
 * faturamento quando a etapa que fecha a conta foi feita.
 *
 * Depois da OPU vem também a ficha da TE: o laboratório leva as duas para a
 * fazenda (pedido de 29/10/2026).
 */
export const proximosPassos = (s, ctx = {}) => {
  if (!s) return [];
  const passos = [];
  const proxima = etapaParaAbrir(s);
  const pendente = proxima && etapasContratadas(s).some(e => e.id === proxima && !e.done) ? proxima : null;

  if (pendente) {
    passos.push(botao('abrir_etapa', { serviceId: s.id, etapa: pendente }, `Lançar ${rotuloDaEtapa(pendente)}`));
    if (temFichaDeCampo(pendente)) passos.push(botao('ficha_de_campo', { serviceId: s.id, etapa: pendente }));
    if (pendente === 'fiv' && etapasContratadas(s).some(e => e.id === 'te' && !e.done)) {
      passos.push(botao('ficha_de_campo', { serviceId: s.id, etapa: 'te' }));
    }
  }

  const jaEmitida = Boolean(ctx.entradaEmitidaPorServico?.[s.id]);
  const entrada = entradaAEmitir(s, ctx.lab, jaEmitida);
  if (entrada.deve) {
    passos.push(botao('emitir_entrada', { serviceId: s.id }, `Emitir entrada (${entrada.quantidade} embriões)`));
  }

  if (prontoParaFaturar(s, ctx)) {
    passos.push(botao('faturar', { serviceId: s.id }, 'Faturar o serviço'));
  }
  return passos;
};

/** Etapas que viraram feitas entre duas versões do serviço, na ordem da corrente. */
export const etapasConcluidasAgora = (antes = {}, depois = {}) =>
  SCHEDULE_CHAIN.filter(e => !antes[e.doneField] && depois[e.doneField]).map(e => e.id);
